// admin/src/screens/Maintenance.jsx — the Maintenance Mode switch (MAINT-MODE §7). Toggle + a
// custom message, the current state shown loudly, and a CONFIRM gate before going dark (the same
// deliberate-override shape as the negative-credits deduct). Every flip is audited server-side
// (admin_audit 'maintenance.set', old→new). Turning OFF and editing the message apply directly;
// only OFF→ON is guarded.

import React, { useEffect, useState } from 'react';
import { useAdmin } from '../App.jsx';
import { Banner, Field } from '../lib/ui.jsx';

export default function Maintenance() {
  const { api, maint, setMaint, refreshMaint } = useAdmin();
  const [message, setMessage] = useState('');
  const [dirty, setDirty] = useState(false);     // the textarea diverges from the saved message
  const [confirm, setConfirm] = useState(false); // OFF→ON needs an explicit confirm
  const [result, setResult] = useState(null);    // { kind:'ok'|'err', text }
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (maint == null) refreshMaint(); }, [maint, refreshMaint]);
  // sync the textarea from the saved message until the user starts editing
  useEffect(() => { if (maint && !dirty) setMessage(maint.message || ''); }, [maint, dirty]);

  const on = !!(maint && maint.on);

  async function save(nextOn) {
    setResult(null); setBusy(true);
    const r = await api.put('admin/settings/maintenance', { on: nextOn, message });
    setBusy(false); setConfirm(false);
    if (r.kind === 'ok') {
      setMaint({ on: r.body.on, message: r.body.message });
      setDirty(false);
      setResult({ kind: 'ok', text: r.body.on ? 'Maintenance mode is ON — the studio app is now dark to non-admins.' : 'Maintenance mode is OFF — the studio app is live.' });
    } else {
      setResult({ kind: 'err', text: r.error || 'Failed to save.' });
    }
  }

  if (maint == null) return <p className="muted">Loading…</p>;

  return (
    <div>
      <h1>Maintenance mode</h1>
      <Banner kind={on ? 'warn' : 'info'}>
        {on
          ? 'The studio app is currently DARK — only admins can reach it; everyone else sees the maintenance page. Renders already running keep going, and paid jobs still finish.'
          : 'The studio app is LIVE. Turn maintenance on to take it dark to non-admins (you keep full access, so you can keep working while it’s down).'}
      </Banner>

      <div className="card" style={{ maxInlineSize: 640 }}>
        <div className="row" style={{ alignItems: 'center', gap: 18 }}>
          <span className="stat"><b className={on ? 'neg' : ''}>{on ? 'ON' : 'OFF'}</b><span>current state</span></span>
          <span className="spacer" />
          <button className={'btn ' + (on ? 'primary' : 'danger')} disabled={busy}
            onClick={() => (on ? save(false) : setConfirm(true))}>
            {on ? 'Turn maintenance OFF' : 'Turn maintenance ON'}
          </button>
        </div>

        <Field label="Maintenance message (shown to users verbatim)" hint="Plain text — line breaks are kept. One message for all languages for now.">
          <textarea rows={3} style={{ inlineSize: '100%', boxSizing: 'border-box' }}
            placeholder="e.g. We’re making improvements and will be back by 3pm SGT."
            value={message} onChange={(e) => { setMessage(e.target.value); setDirty(true); }} />
        </Field>
        <div className="row">
          <span className="hint">Saving the message doesn’t flip the switch{on ? '' : ' — it just stores the text for next time'}.</span>
          <span className="spacer" />
          <button className="btn" disabled={busy || !dirty} onClick={() => save(on)}>Save message</button>
        </div>

        {confirm && (
          <div className="confirm">
            <b>Take the studio app dark?</b>
            <p className="muted" style={{ margin: '4px 0 8px' }}>
              Every non-admin visitor is blocked — signed-out users see the maintenance page, signed-in users are cut off mid-session. In-flight renders keep running and paid jobs still finish. You keep full access.
            </p>
            <div className="row">
              <button className="btn danger" disabled={busy} onClick={() => save(true)}>Yes, turn maintenance ON</button>
              <button className="btn ghost" onClick={() => setConfirm(false)}>Cancel</button>
            </div>
          </div>
        )}
        {result && <Banner kind={result.kind === 'ok' ? 'ok' : 'err'}>{result.text}</Banner>}
      </div>
    </div>
  );
}
